import { deleteData, getData, postData, putData } from "../api/api";

export type EmbedThemeMode = "LIGHT" | "DARK" | "AUTO";
export type EmbedPosition = "BOTTOM_RIGHT" | "BOTTOM_LEFT";
export type EmbedRole = "tenant_admin" | "user" | "reporter" | "viewer";

export const EMBED_ROLES: EmbedRole[] = ["tenant_admin", "user", "reporter", "viewer"];

export type EmbedTheme = {
  primaryColor?: string | null;
  mode: EmbedThemeMode;
  position: EmbedPosition;
  launcherLabel?: string | null;
  welcomeMessage?: string | null;
  locale?: string | null;
};

export type EmbedDefaultScope = {
  indices: string[];
  adaptorIds: string[];
  openApiCatalogIds: string[];
  allowedSlashCommands: string[];
};

export type EmbedFeatures = {
  saveWidget: boolean;
  addToDashboard: boolean;
  mutations: boolean;
  export: boolean;
  warehouseJobs: boolean;
  forecast: boolean;
  showHistory: boolean;
  scopePickers: boolean;
  hostContext: boolean;
  pageContent: boolean;
  formFill: boolean;
};

export const EMBED_FEATURE_KEYS: (keyof EmbedFeatures)[] = [
  "saveWidget",
  "addToDashboard",
  "mutations",
  "export",
  "warehouseJobs",
  "forecast",
  "showHistory",
  "scopePickers",
  "hostContext",
  "pageContent",
  "formFill",
];

/** `ticket`: one-time login tickets from an integration client (platform-wide embeds only). */
export type EmbedAuthProviders = { password: boolean; google: boolean; ticket?: boolean };

export type EmbedRateLimit = {
  messagesPerUserPerMinute: number;
  messagesPerKeyPerMinute: number;
};

export type EmbedConfig = {
  id: string;
  name: string;
  enabled: boolean;
  publicKey: string;
  previousKey?: string | null;
  previousKeyGraceUntil?: string | null;
  rotatedAt?: string | null;
  workspaceId?: string | null;
  workspaceName?: string | null;
  allowedOrigins: string[];
  theme: EmbedTheme;
  defaultScope: EmbedDefaultScope;
  features: EmbedFeatures;
  rememberSession: boolean;
  allowedRoles: EmbedRole[];
  authProviders: EmbedAuthProviders;
  rateLimit: EmbedRateLimit;
  lockedBySuperAdmin: boolean;
  loaderUrl: string;
  appOrigin: string;
  createdBy?: string;
  createdDate?: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
};

export type EmbedConfigPayload = {
  name: string;
  enabled: boolean;
  workspaceId: string | null;
  allowedOrigins: string[];
  theme: EmbedTheme;
  defaultScope: EmbedDefaultScope;
  features: EmbedFeatures;
  rememberSession: boolean;
  allowedRoles: EmbedRole[];
  authProviders: EmbedAuthProviders;
  rateLimit: EmbedRateLimit;
  lockedBySuperAdmin: boolean;
};

export type EmbedUsageDay = { date: string; messages: number; activeUsers: number };

export type EmbedUsage = {
  days: number;
  conversations: number;
  messages: number;
  activeUsers: number;
  logins: number;
  deniedLogins: number;
  daily: EmbedUsageDay[];
};

export type EmbedAllowance = { allowed: boolean; max: number | null; used: number };

/** Platform = Super Admin (all embeds); workspace = workspace admin (own workspace, package allowance). */
export type EmbedScope = "platform" | "workspace";

export function embedConfigApi(scope: EmbedScope) {
  const base = scope === "platform" ? "/admin/embeds" : "/workspaces/me/embeds";
  const item = (id: string) => `${base}/${encodeURIComponent(id)}`;
  return {
    list: () => getData(base) as Promise<EmbedConfig[]>,
    create: (payload: EmbedConfigPayload) => postData(base, payload) as Promise<EmbedConfig>,
    update: (id: string, payload: EmbedConfigPayload) => putData(item(id), payload) as Promise<EmbedConfig>,
    remove: (id: string) => deleteData(item(id)) as Promise<void>,
    rotate: (id: string, graceDays: number) =>
      postData(`${item(id)}/rotate?graceDays=${encodeURIComponent(String(graceDays))}`, {}) as Promise<EmbedConfig>,
    usage: (id: string, days = 30) =>
      getData(`${item(id)}/usage?days=${encodeURIComponent(String(days))}`) as Promise<EmbedUsage>,
    /** Workspace scope only. */
    allowance: () => getData(`${base}/allowance`) as Promise<EmbedAllowance>,
  };
}

export const embedConfigService = embedConfigApi("platform");
